import React, { useCallback, useEffect, useMemo, useState } from "react";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import { Row, Col, Container, InputGroup, FormControl, Button } from "react-bootstrap";
import { Link, useHistory } from "react-router-dom";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import dlt from "assets/svg/delete.svg";
import DataTable, { DataTableColumn } from "components/common/DataTable";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import CommonModal from "components/common/CommonModal";
import { canAccess } from "utils/authorize-action";
import { isHttpSuccess } from "utils/functions";
import { HttpStatus, FETCH_GATEWAY_FAIL_MESSAGE, ACTION_FAIL_MESSAGE, ADD_NETWORK_BUTTON_LABEL, HeartbeatUpdateAfter } from "constant";
import { useOrganisation } from "hooks/organization";
import { getAllRegistry, getGWReading, getRegistryConfig, deleteRegistry } from "service/gatewayService";
import { checkActive, isNetworkGateway } from "utils/gatewayFunctions";
import { fetchTags as fetchTagsCommon } from "./common/tagService";
import {
  countDevicesByTagUIDSAIDs,
  addDeviceToUIDSAIDsMap,
  isUIDSetZoneNotRoom,
  buildUIDToDeviceMap,
  hasMatchingRegistryNameOrNick,
} from "./common/deviceUtils";
import { countBusDevices } from "./common/networksListBusCounts";

// Type for a network row
interface NetworkRow {
  networkName: string;
  panlNetworkId: string;
  status: string;
  statusSortValue?: number; // Numeric value for proper status sorting
  heartbeat?: number | null;
  info?: boolean;
  offlineSince?: string | null;
  zones?: number | null;
  rooms?: number | null;
  groups?: number | null;
  sensors?: number | null;
  controllers?: number | null;
  auxiliaryHubs?: number | null;
  hubs?: number | null;
}

const NetworksList = (): JSX.Element => {
  const history = useHistory();
  const [allNetworks, setAllNetworks] = useState<NetworkRow[]>([]);
  const [initialNetworks, setInitialNetworks] = useState<NetworkRow[]>([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [modalShow, setModalShow] = useState(false); // delete confirm modal
  const [showDownloadModal, setShowDownloadModal] = useState(false); // download info modal
  const [networkToDelete, setNetworkToDelete] = useState<NetworkRow | null>(null);
  const [isForbiddenResource, setIsForbiddenResource] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { currentOrgId } = useOrganisation();

  // Helper to determine if a network should be treated as "Not yet connected"
  // based on the same counts logic used for the NetworkDetails early-return view.
  const isNetworkNotYetConnected = (n: NetworkRow): boolean => {
    const counts = [
      n.zones,
      n.rooms,
      n.groups,
      n.sensors,
      n.controllers,
      n.auxiliaryHubs,
      n.hubs,
    ];

    const countsLoaded = counts.some((value) => value !== null && value !== undefined);
    const hasAnyContent = counts.some((value) => (value ?? 0) > 0);

    return !!n.info && countsLoaded && !hasAnyContent;
  };

  // Render status for each network row.
  // - If network is onboarded (info = true) but has no zones/rooms/groups/sensors/controllers/aux hubs/hubs,
  //   we treat it as "Not yet connected" to match the NetworkDetails page behaviour.
  // - Otherwise we fall back to the generic checkActive(heartbeat, info) logic
  //   which shows "Online", "Offline from <time>", or "Not On-boarded".
  const renderNetworkStatus = (n: NetworkRow) => {
    const counts = [
      n.zones,
      n.rooms,
      n.groups,
      n.sensors,
      n.controllers,
      n.auxiliaryHubs,
      n.hubs,
    ];

    const countsLoaded = counts.some((value) => value !== null && value !== undefined);
    const hasAnyContent = counts.some((value) => (value ?? 0) > 0);

    // If the network is onboarded but has no related entities/devices even after counts are loaded,
    // show "Not yet connected"
    if (n.info && countsLoaded && !hasAnyContent) {
      return (
        <>
          <span
            style={{
              color: "#ff3860",
              marginRight: 8,
              fontSize: "16px",
            }}
          >
            &#9679;
          </span>
          <span>Not yet connected</span>
        </>
      );
    }

    // Default behaviour: reuse shared gateway/network status helper.
    return checkActive(n.heartbeat ?? 0, n.info ?? false);
  };

  useEffect(() => {
    const fetch = async () => {
      if (!currentOrgId) {
        return;
      }

      const res: any = await getAllRegistry(currentOrgId);

      if (res.status === HttpStatus.FORBIDDEN) {
        setIsForbiddenResource(true);
        setIsPageLoading(false);
        return;
      }

      if (!isHttpSuccess(res.status)) {
        // API failed, show empty list
        setAllNetworks([]);
        setInitialNetworks([]);
        showErrorAlert(getAPIError(res, FETCH_GATEWAY_FAIL_MESSAGE));
        setIsPageLoading(false);
        return;
      }

      let combinedData: Array<any> = [];
      const readingDevicesByGateway = new Map<string, any[]>();
      if (res.data.length > 0) {
        const hbRes: any = await getGWReading();
        const lastData = hbRes.data.map((i: any) => {
          readingDevicesByGateway.set(i.gateway_id, i.devices ?? []);
          return {
            heartbeat: i.heartbeat,
            gateway_id: i.gateway_id,
          };
        });

        const cloned = res.data;
        for (const element of cloned) {
          for (const last of lastData) {
            if (element.gateway_id === last.gateway_id) {
              element.heartbeat = last.heartbeat;
            }
          }
        }
        combinedData = [...cloned];
      }

      // Filter gateways that look like PanL Networks (PN0...)
      const pnGateways = combinedData.filter(
        (g: any) => isNetworkGateway(g)
      );

      // Map into NetworkRow shape
      const mapped: NetworkRow[] = pnGateways.map((g: any) => {
        // Store heartbeat and info for checkActive function
        const heartbeat = g.heartbeat ?? null;
        const info = !!g.info; // Network is onboarded if it has info object
        const status = heartbeat ? "Online" : "Offline";
        let offlineSince = null;
        if (status === "Offline" && g.heartbeat) {
          offlineSince = g.heartbeat;
        }

        // Calculate statusSortValue for proper sorting
        // Logic matches checkActive function:
        // - Not On-boarded (info = false): 0 (sorts first)
        // - Offline (info = true, but difference >= HeartbeatUpdateAfter): heartbeat timestamp (newer = higher)
        // - Online (info = true, difference < HeartbeatUpdateAfter): current timestamp (sorts last/highest)
        let statusSortValue: number;
        if (!info) {
          // Not On-boarded - use 0 so it sorts first
          statusSortValue = 0;
        } else if (heartbeat) {
          const timestamp = Math.round(Date.now() / 1000);
          const difference = timestamp - heartbeat;
          if (difference < HeartbeatUpdateAfter) {
            // Online - use current timestamp so it sorts highest
            statusSortValue = timestamp;
          } else {
            // Offline - use heartbeat timestamp so newer offline times sort higher
            statusSortValue = heartbeat;
          }
        } else {
          // No heartbeat but has info - treat as offline with 0 timestamp
          statusSortValue = 0;
        }

        return {
          networkName: g.name,
          panlNetworkId: g.gateway_id,
          status,
          statusSortValue,
          heartbeat,
          info,
          offlineSince,
          zones: null,
          rooms: null,
          groups: null,
          sensors: null,
          controllers: null,
          auxiliaryHubs: null,
          hubs: null,
        } as NetworkRow;
      });

      setAllNetworks(mapped);
      setInitialNetworks(mapped);

      // Fetch counts for each network and wait for completion before hiding loader.
      // Reuse the registry list and org-level reading data we already fetched above.
      try {
        await fetchNetworkCounts(mapped, combinedData, readingDevicesByGateway);
      } catch (error) {
        console.error("Error fetching network counts:", error);
      } finally {
        setIsPageLoading(false);
      }
    };

    // Function to fetch tags from API
    const fetchTags = async (organizationId: string) => {
      try {
        const tags = await fetchTagsCommon(organizationId);
        return tags;
      } catch (error) {
        console.error("NetworksList: Error fetching tags:", error);
        return [];
      }
    };

    // Helper to extract UID strings from a single tag's devices array
    const extractUIDsFromTagDevices = (
      devices: any[],
      gatewayId: string
    ): Set<string> => {
      const uids = new Set<string>();

      if (!devices || !Array.isArray(devices)) {
        return uids;
      }

      for (const device of devices) {
        if (!Array.isArray(device) || device.length === 0) {
          continue;
        }
        if (String(device[0] ?? "") !== gatewayId) {
          continue;
        }

        const uidCandidates = [device[1]].filter(Boolean);
        for (const uid of uidCandidates) {
          if (uid) {
            uids.add(String(uid));
          }
        }
      }

      return uids;
    };

    // Function to extract UIDs from tags by type and create a map
    // Tags have a devices array with [device_id, said] pairs
    const extractTagUIDsByType = (tags: any[], gatewayId: string) => {
      const tagUIDsMap = new Map<string, Map<string, Set<string>>>();

      for (const type of ["zone", "room", "group", "hub"]) {
        const typeMap = new Map<string, Set<string>>();

        const filteredTags = tags.filter(
          (tag: any) => tag.value?.startsWith(`${type}/`)
        );

        for (const tag of filteredTags) {
          // Extract tag name from value (e.g., "zone/Zone 1" -> "Zone 1")
          const tagName = tag.value?.replace(`${type}/`, "") ?? "";
          const uids = extractUIDsFromTagDevices(tag.devices ?? [], gatewayId);

          if (tagName && uids.size > 0) {
            typeMap.set(tagName, uids);
          }
        }

        tagUIDsMap.set(type, typeMap);
      }

      return tagUIDsMap;
    };

    // Helper function to extract UID from a single device
    const extractUIDFromDevice = (device: any): string | null => {
      if (device?.UID) {
        return String(device.UID);
      } else if (Array.isArray(device) && device.length > 0) {
        return String(device[0]);
      } else if (typeof device === 'string') {
        return device;
      }
      return null;
    };

    // Function to extract UIDs from registry devices
    // Registry devices are in data[i].info.devices[j] format
    const extractRegistryUIDs = (registryData: any[]): Set<string> => {
      const uids = new Set<string>();

      if (!Array.isArray(registryData)) {
        return uids;
      }

      for (const item of registryData) {
        if (!item?.info?.devices || !Array.isArray(item.info.devices)) {
          continue;
        }

        for (const device of item.info.devices) {
          const uid = extractUIDFromDevice(device);
          if (uid) {
            uids.add(uid);
          }
        }
      }

      return uids;
    };

    // Helper function to extract UID/SAID mappings from tag devices
    const extractUIDSAIDsFromTagDevices = (
      devices: any[],
      gatewayId: string
    ): Map<string, Set<number>> => {
      const uidSaids = new Map<string, Set<number>>();
      for (const device of devices) {
        if (!Array.isArray(device) || String(device[0] ?? "") !== gatewayId) {
          continue;
        }
        addDeviceToUIDSAIDsMap(device, uidSaids);
      }
      return uidSaids;
    };

    // Function to extract UID-SAID mappings from tags by type (for accurate room counting)
    const extractTagUIDSAIDsByType = (tags: any[], gatewayId: string) => {
      const tagUIDSAIDsMap = new Map<string, Map<string, Map<string, Set<number>>>>();

      for (const type of ["zone", "room", "group", "hub"]) {
        const typeMap = new Map<string, Map<string, Set<number>>>();

        const filteredTags = tags.filter(
          (tag: any) => tag.value?.startsWith(`${type}/`)
        );

        for (const tag of filteredTags) {
          // Extract tag name from value (e.g., "zone/Zone 1" -> "Zone 1")
          const tagName = tag.value?.replace(`${type}/`, "") ?? "";
          let uidSaids = new Map<string, Set<number>>();

          if (tag.devices && Array.isArray(tag.devices)) {
            uidSaids = extractUIDSAIDsFromTagDevices(tag.devices, gatewayId);
          }

          if (tagName && uidSaids.size > 0) {
            typeMap.set(tagName, uidSaids);
          }
        }

        tagUIDSAIDsMap.set(type, typeMap);
      }

      return tagUIDSAIDsMap;
    };


    const computeCountsForNetwork = async (
      network: NetworkRow,
      allRegistries: any[],
      tags: any[],
      gatewayConfigMap: Map<string, any>,
      readingDevicesByGateway: Map<string, any[]>
    ) => {
      try {
        const gatewayId = network.panlNetworkId;
        const tagUIDsMap = extractTagUIDsByType(tags, gatewayId);
        const tagUIDSAIDsMap = extractTagUIDSAIDsByType(tags, gatewayId);

        // Locate registry entry and extract devices/UIDs for this gateway
        const registryItem = allRegistries.find(
          (reg: any) => reg?.gateway_id === gatewayId
        );
        const devices = registryItem?.info?.devices ?? [];
        const registryUIDs = registryItem ? extractRegistryUIDs([registryItem]) : new Set<string>();
        const uidToDeviceMap = buildUIDToDeviceMap(devices);

        // Helper function to check if tag UIDs match registry UIDs
        const hasMatchingUID = (tagUIDs: Set<string>, registryUIDsLocal: Set<string>): boolean => {
          for (const uid of Array.from(tagUIDs)) {
            if (registryUIDsLocal.has(uid)) {
              return true;
            }
          }
          return false;
        };

        // Helper function to count matching tags for a type (for zones, groups, hubs)
        const isRoomTagActuallyZone = (uids: Set<string>, devicesList: any[]): boolean =>
          isUIDSetZoneNotRoom(uids, devicesList);

        const countMatchingTags = (
          typeTags: Map<string, Set<string>>,
          registryUIDsLocal: Set<string>
        ): number => {
          let matchCount = 0;
          for (const [tagName, tagUIDs] of Array.from(typeTags.entries())) {
            if (
              hasMatchingUID(tagUIDs, registryUIDsLocal) &&
              hasMatchingRegistryNameOrNick(tagName, tagUIDs, registryUIDsLocal, uidToDeviceMap)
            ) {
              matchCount++;
            }
          }
          return matchCount;
        };

        // Count zones, groups, and hubs using tag matching (same as before)
        const zonesCount = countMatchingTags(
          tagUIDsMap.get("zone") ?? new Map<string, Set<string>>(),
          registryUIDs
        );
        const groupsCount = countMatchingTags(
          tagUIDsMap.get("group") ?? new Map<string, Set<string>>(),
          registryUIDs
        );
        const hubsCount = countMatchingTags(
          tagUIDsMap.get("hub") ?? new Map<string, Set<string>>(),
          registryUIDs
        );

        // Helper to count rooms for this gateway
        const roomTagUIDSAIDs = tagUIDSAIDsMap.get("room") ?? new Map<string, Map<string, Set<number>>>();
        const roomTagUIDs = tagUIDsMap.get("room") ?? new Map<string, Set<string>>();

        const roomsCount = (() => {
          let rooms = 0;

          for (const [tagName, uidSaids] of Array.from(roomTagUIDSAIDs.entries())) {
            const tagUIDs = roomTagUIDs.get(tagName) ?? new Set<string>();
            if (!hasMatchingUID(tagUIDs, registryUIDs)) {
              continue;
            }
            if (!hasMatchingRegistryNameOrNick(tagName, tagUIDs, registryUIDs, uidToDeviceMap)) {
              continue;
            }

            if (isRoomTagActuallyZone(tagUIDs, devices)) {
              continue;
            }

            const counts = countDevicesByTagUIDSAIDs(uidSaids, devices);
            if (counts.rooms > 0) {
              rooms++;
            }
          }

          return rooms;
        })();

        // Get sensor, controller, and auxiliary hub counts using already fetched config
        // and org-level reading data (devices) instead of per-gateway /reading2
        const registryConfigRes = gatewayConfigMap.get(gatewayId);
        const statusDevices = readingDevicesByGateway.get(gatewayId) ?? [];

        let sensors = null;
        let controllers = null;
        let auxiliaryHubs = null;

        if (registryConfigRes && isHttpSuccess(registryConfigRes.status)) {
          const configList = registryConfigRes.data ?? [];
          const busCounts = countBusDevices(devices, configList, statusDevices);

          sensors = busCounts.sensors;
          controllers = busCounts.controllers;
          auxiliaryHubs = busCounts.auxiliaryHubs;
        }

        return {
          gatewayId,
          zones: zonesCount,
          rooms: roomsCount,
          groups: groupsCount,
          hubs: hubsCount,
          sensors,
          controllers,
          auxiliaryHubs
        };
      } catch (error) {
        console.error("NetworksList: Error computing counts for gateway", network.panlNetworkId, error);
        return {
          gatewayId: network.panlNetworkId,
          zones: null,
          rooms: null,
          groups: null,
          hubs: null,
          sensors: null,
          controllers: null,
          auxiliaryHubs: null
        };
      }
    };

    // Function to fetch counts for networks using registry and tags APIs
    const fetchNetworkCounts = async (
      networks: NetworkRow[],
      allRegistries: any[],
      readingDevicesByGateway: Map<string, any[]>
    ) => {
      if (!currentOrgId) {
        return;
      }

      // Fetch config for sensor/controller counts (use org-level reading data instead of per-gateway /reading2)
      const gatewayConfigMap = new Map<string, any>();

      // Only consider onboarded networks when fetching config/status
      const onboardedGatewayIds = networks
        .filter((n) => n.info)
        .map((n) => n.panlNetworkId);

      await Promise.all(
        onboardedGatewayIds.map(async (gatewayId: string) => {
          try {
            const configRes = await getRegistryConfig(gatewayId);
            if (isHttpSuccess(configRes.status)) {
              gatewayConfigMap.set(gatewayId, configRes);
            }
          } catch (error) {
            console.error("NetworksList: Error fetching config for gateway", gatewayId, error);
          }
        })
      );

      const tags = await fetchTags(currentOrgId);

      // Only compute counts for onboarded networks; leave others as null to display "-" in the table.
      const countPromises = networks
        .filter((network) => network.info)
        .map((network) =>
          computeCountsForNetwork(
            network,
            allRegistries,
            tags,
            gatewayConfigMap,
            readingDevicesByGateway
          )
        );

      // Wait for all counts to be fetched
      const counts = await Promise.all(countPromises);

      // Create a Map for O(1) lookups instead of using find
      const countsMap = new Map<string, any>();
      for (const count of counts) {
        if (count.gatewayId) {
          countsMap.set(count.gatewayId, count);
        }
      }

      // Helper function to calculate statusSortValue
      const calculateStatusSortValue = (network: NetworkRow): number => {
        if (!network.info) {
          // Not On-boarded - use 0 so it sorts first
          return 0;
        }
        if (network.heartbeat) {
          const timestamp = Math.round(Date.now() / 1000);
          const difference = timestamp - network.heartbeat;
          if (difference < HeartbeatUpdateAfter) {
            // Online - use current timestamp so it sorts highest
            return timestamp;
          }
          // Offline - use heartbeat timestamp so newer offline times sort higher
          return network.heartbeat;
        }
        // No heartbeat but has info - treat as offline with 0 timestamp
        return 0;
      };

      // Helper function to update a network with counts
      const updateNetworkWithCounts = (network: NetworkRow) => {
        const count = countsMap.get(network.panlNetworkId);
        const updatedNetwork = {
          ...network,
          zones: count?.zones ?? network.zones,
          rooms: count?.rooms ?? network.rooms,
          groups: count?.groups ?? network.groups,
          hubs: count?.hubs ?? network.hubs,
          sensors: count?.sensors ?? network.sensors,
          controllers: count?.controllers ?? network.controllers,
          auxiliaryHubs: count?.auxiliaryHubs ?? network.auxiliaryHubs
        };
        // Recalculate statusSortValue in case heartbeat changed
        updatedNetwork.statusSortValue = calculateStatusSortValue(updatedNetwork);
        return updatedNetwork;
      };

      // Update networks with counts
      setAllNetworks((prevNetworks) =>
        prevNetworks.map(updateNetworkWithCounts)
      );

      // Also update initial networks
      setInitialNetworks((prevNetworks) =>
        prevNetworks.map(updateNetworkWithCounts)
      );
    };

    if (canAccess("gateway:read")) {
      fetch();
    } else {
      setIsPageLoading(false);
      setIsForbiddenResource(true);
    }
  }, [currentOrgId]);

  const searchNetwork = (value: string) => {
    setSearchQuery(value);
    if (value === "") {
      setAllNetworks(initialNetworks);
    } else {
      const queryList = initialNetworks.filter((n) =>
        n.networkName.toLowerCase().includes(value.toLowerCase())
      );
      setAllNetworks(queryList);
    }
  };

  const openDeleteConfirmModal = (network: NetworkRow) => {
    setNetworkToDelete(network);
    // For "Not yet connected" networks don't need to show the
    // "Download Data" modal, just go straight to the delete confirm.
    if (isNetworkNotYetConnected(network)) {
      setModalShow(true);
    } else {
      setShowDownloadModal(true);
    }
  };

  const closeDownloadModal = () => {
    setShowDownloadModal(false);
    setNetworkToDelete(null);
  };

  const proceedToDeleteModal = () => {
    setShowDownloadModal(false);
    setModalShow(true);
  };

  const closeConfirmModal = () => {
    setModalShow(false);
    setNetworkToDelete(null);
  };

  const handleDeleteNetwork = async () => {
    if (!networkToDelete) return;

    // Always close the confirmation modal
    setModalShow(false);

    try {
      const response = await deleteRegistry(networkToDelete.panlNetworkId);

      if (isHttpSuccess(response.status)) {
        // Remove the deleted network from local state
        setAllNetworks((prev) =>
          prev.filter((n) => n.panlNetworkId !== networkToDelete.panlNetworkId)
        );
        setInitialNetworks((prev) =>
          prev.filter((n) => n.panlNetworkId !== networkToDelete.panlNetworkId)
        );

        showSuccessAlert({ message: "Network has been deleted successfully." });
      } else {
        showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
      }
    } catch (error) {
      console.error("NetworksList: Error deleting network:", error);
      showErrorAlert({
        message:
          "An error occurred while deleting the network. Please try again later.",
      });
    } finally {
      setNetworkToDelete(null);
    }
  };

  const handleAddNetwork = useCallback(() => {
    history.push("/networks-add");
  }, [history]);

  const renderEmpty = useMemo(
    () => (
      <Row>
        <Col sm="12">
          <div className="empty-widget text-center">
            <h3 className="mb-3">No Networks Added</h3>
            <h5 className="mb-3">Add a new network</h5>
            <div className="gateway-empty-container">

              <HoverAuthorizeTooltip permission="network:create" className="w-50">
                <Button
                  variant="primary"
                  className="add-gateway-btn mr-3"
                  onClick={handleAddNetwork}
                >
                  + Add Network
                </Button>
              </HoverAuthorizeTooltip>
            </div>

          </div>
        </Col>
      </Row>
    ),
    [handleAddNetwork]
  );

  const renderSearchEmpty = useMemo(
    () => (
      <div className="mt-2 text-center">
        No networks found.
      </div>
    ),
    []
  );

  const columns: DataTableColumn<NetworkRow>[] = [
    {
      label: "Network Name",
      key: "networkName",
      render: (n: NetworkRow) => (
        <Link to={`/network-details/${n.panlNetworkId}`}>
          {n.networkName}
        </Link>
      ),
      colProps: { xs: 12, sm: 2, md: 2, lg: 2 },
    },
    {
      label: "Network ID",
      key: "panlNetworkId",
      render: (n: NetworkRow) => <span>{n.panlNetworkId}</span>,
      colProps: { sm: 2 },
    },
    {
      label: "Status",
      key: "status",
      sortKey: "statusSortValue", // Use statusSortValue for proper sorting
      render: (n: NetworkRow) => (
        <span style={{ display: "flex", alignItems: "center" }}>
          {renderNetworkStatus(n)}
        </span>
      ),
      colProps: { xs: 12, sm: 2, md: 2, lg: 2 },
    },
    {
      label: "Zones",
      key: "zones",
      render: (n: NetworkRow) => n.zones ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    {
      label: "Rooms",
      key: "rooms",
      render: (n: NetworkRow) => n.rooms ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    {
      label: "Groups",
      key: "groups",
      render: (n: NetworkRow) => n.groups ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    {
      label: "Sensors",
      key: "sensors",
      render: (n: NetworkRow) => n.sensors ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    { 
      label: "Actuators", 
      key: "controllers", 
      render: (n: NetworkRow) => n.controllers ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    {
      label: "Aux Hubs",
      key: "auxiliaryHubs",
      render: (n: NetworkRow) => n.auxiliaryHubs ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    {
      label: "Hubs",
      key: "hubs",
      render: (n: NetworkRow) => n.hubs ?? "-",
      colProps: { className: "d-none d-sm-block" },
    },
    {
      label: "Action",
      key: "action",
      sortable: false,
      render: (n: NetworkRow) => (
        <div className="gatewayList-row-end">
          <div className="gatewayList-row-delete-action">
            <HoverAuthorizeTooltip permission="network:delete">
              <button
                type="button"
                className="btn p-0"
                aria-label="Delete network"
                onClick={() => openDeleteConfirmModal(n)}
              >
                <img src={dlt} alt="" />
              </button>
            </HoverAuthorizeTooltip>
          </div>
          <div className="gatewayList-row-detail-action">
            <Link to={`/network-details/${n.panlNetworkId}`}>
              <i className="material-icons right">keyboard_arrow_right</i>
            </Link>
          </div>
        </div>
      ),
      colProps: { xs: 12, sm: 1, md: 1, lg: 1 },
    },
  ];

  return (
    <ContentWrapper
      title="Networks"
      isLoading={isPageLoading}
      isForbiddenResource={isForbiddenResource}
    >
      <div className="page-content gateway-list-page">
        <Container fluid>
          <Row>
            <Col sm={12}>
              <h5 className="page-title">Networks</h5>
            </Col>
          </Row>
          <Row>
            <Col
              sm={12}
              className="d-md-flex justify-content-end order-md-2 order-1"
            >
              {initialNetworks.length > 0 && (
                <>
                  <HoverAuthorizeTooltip permission="network:create">
                    <Button
                      variant="primary"
                      className="add-gateway mr-3"
                      onClick={handleAddNetwork}
                    >
                      {ADD_NETWORK_BUTTON_LABEL}
                    </Button>
                  </HoverAuthorizeTooltip>
                  <div className="search">
                    <InputGroup>
                      <FormControl
                        type="text"
                        placeholder="Search"
                        aria-label="search-network"
                        aria-describedby="button-addon2"
                        value={searchQuery}
                        onChange={(e) => searchNetwork(e.target.value)}
                      />
                    </InputGroup>
                  </div>
                </>
              )}
            </Col>
          </Row>
          {!isPageLoading && (
            <DataTable
              columns={columns}
              data={allNetworks}
              emptyContent={searchQuery && initialNetworks.length > 0 ? renderSearchEmpty : renderEmpty}
              rowKey={(row: NetworkRow, idx: number) => row.panlNetworkId + idx}
              hideHeadersWhenEmpty={initialNetworks.length === 0}
              className={allNetworks.length === 0 && initialNetworks.length === 0 ? "cstm-table networks-list-table mt-0" : "cstm-table networks-list-table mt-4"}
            />
          )}
          <CommonModal
            show={showDownloadModal}
            onClose={closeDownloadModal}
            title="Download Data"
            icon={null}
            actions={
              <>
                <Button variant="secondary" onClick={closeDownloadModal}>
                  CANCEL
                </Button>
                <Button
                  style={{ background: "#00D1B2", borderColor: "#00D1B2" }}
                  onClick={proceedToDeleteModal}
                >
                  PROCEED
                </Button>
              </>
            }
          >
            <p>
              Before deleting the network, ensure all network data (including sensors and actuators) has been downloaded.
            </p>
            <p>
              By proceeding with delete network, you will permanently lose all associated data including sensors and actuators.
            </p>
          </CommonModal>
          <CommonModal
            show={modalShow && !!networkToDelete}
            onClose={closeConfirmModal}
            title="Delete Network?"
            className="danger"
            icon={
              <span
                className="material-icons danger"
              >
                delete
              </span>
            }
            actions={
              <>
                <Button variant="secondary" onClick={closeConfirmModal}>
                  CANCEL
                </Button>
                <Button variant="danger" onClick={handleDeleteNetwork}>
                  DELETE
                </Button>
              </>
            }
          >
            <p>
              Are you sure you want to delete <b>{networkToDelete?.networkName ?? ""}</b>? This cannot be undone.
            </p>
          </CommonModal>
        </Container>
      </div>
    </ContentWrapper>
  );
};

export default NetworksList;
